<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import AccountModal from "@/components/modals/account-modal.vue";
import DatePicker from "vue2-datepicker";
import PrintOptions from "@/components/modals/print-options.vue";

export default {
    name: "general-ledger",
    components: {
        Layout,
        PageHeader,
        AccountModal,
        DatePicker,
        PrintOptions
    },
    data() {
        return {
            title: "General Ledger",
            items: [
                {
                    text: "Dashboards",
                    href: "/",
                },
                {
                    text: "Finance",
                    href: "#",
                },
                {
                    text: "General Ledger",
                    active: true,
                },
            ],
            // Mock data
            transactions: [],
            accounts: [],
            
            // Filters
            filter: null,
            selectedAccount: null,
            selectedAccountName: "",
            dateFrom: new Date(`01-01-${new Date().getFullYear()}`),
            dateTo: new Date(`12-31-${new Date().getFullYear()}`),
            
            // Table settings
            loading: false,
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            selectedFC: null,
            datLoading: false,
            
            // Table fields
            fields: [
                {
                    key: "date",
                    label: "Date",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                    formatter: (value) => {
                        return new Date(value).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit'
                        });
                    }
                },
                {
                    key: "reference",
                    label: "Reference",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "particulars",
                    label: "Particulars",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-left"
                },
                {
                    key: "debit",
                    label: "Debit",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-right",
                    formatter: (value) => {
                        return value > 0 ? this.formatCurrency(value) : '-';
                    }
                },
                {
                    key: "credit",
                    label: "Credit",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-right",
                    formatter: (value) => {
                        return value > 0 ? this.formatCurrency(value) : '-';
                    }
                },
                {
                    key: "balance",
                    label: "Balance",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-right",
                    formatter: (value) => {
                        return this.formatCurrency(value);
                    }
                }
            ],
            fundClusters: []
        };
    },
    computed: {
        filteredTransactions() {
            return this.transactions;
        },
        totalRows() {
            return this.filteredTransactions.reduce((total, parent) => {
                return total + parent.generalLedgerTransactions.length;
            }, 0);
        },
        totalDebit() {
            return this.transactions.reduce((total, parent) => {
                const parentDebit = parent.generalLedgerTransactions.reduce((pTotal, transaction) => {
                    return pTotal + (transaction.debit || 0);
                }, 0);
                return total + parentDebit;
            }, 0);
        },
        totalCredit() {
            return this.transactions.reduce((total, parent) => {
                const parentCredit = parent.generalLedgerTransactions.reduce((pTotal, transaction) => {
                    return pTotal + (transaction.credit || 0);
                }, 0);
                return total + parentCredit;
            }, 0);
        },
        currentBalance() {
            return this.totalDebit - this.totalCredit;
        }
    },
    watch: {
        async selectedFC(newVal){
            if(!newVal){
                this.transactions = [];
                return
            }
            this.datLoading = true;
            await this.getData(newVal, null);
            this.datLoading = false;
        },
        async dateFrom(){
            this.datLoading = true;
            await this.changeData(this.selectedFC, this.selectedAccount ? this.selectedAccount.id : null);
            this.datLoading = false;
        },
        async dateTo(){
            this.datLoading = true;
            await this.changeData(this.selectedFC, this.selectedAccount ? this.selectedAccount.id : null);
            this.datLoading = false;
        },
        async selectedAccount(newVal){
            this.datLoading = true;
            await this.changeData(this.selectedFC, newVal ? newVal.id : null);
            this.datLoading = false;
        }
    },
    async created() {
        this.loading = true;
        await this.getAccounts();
        await this.getFundClusters();
        this.selectedFC = this.fundClusters.length > 1 ? this.fundClusters[1].value : null;
        this.loading = false;
    },
    methods: {
        async getData(fcId, acId){
            if(!fcId){
                return this.showToast('Must select fund cluster', 'warning');
            }
            if(!acId){
                await this.$store
                    .dispatch("generalledger/GetGLByFundCluster", {
                        campusId: this.$store.state.data.selectedCampus.id,
                        fundClusterId: fcId,
                        dateFrom: this.getDate(this.dateFrom),
                        dateTo: this.getDate(this.dateTo),
                        removeLoading: true
                    })
                    .then((res) => {
                        if (res.data.error) {
                            return this.showToast(res.data.errorMessage, "error");
                        }
                        this.transactions = res.data;
                    })
                .catch((error) => {
                    this.showToast("Cannot fetch accounts. Please contact your administrator", "error");
                });
            }else{
                await this.$store
                    .dispatch("generalledger/GetGLByAccount", {
                        campusId: this.$store.state.data.selectedCampus.id,
                        accountId: acId,
                        fundClusterId: fcId,
                        dateFrom: this.getDate(this.dateFrom),
                        dateTo: this.getDate(this.dateTo),
                        removeLoading: true
                    })
                    .then((res) => {
                        if (res.data.error) {
                            return this.showToast(res.data.errorMessage, "error");
                        }
                        this.transactions = [res.data];
                    })
                .catch((error) => {
                    this.showToast("Cannot fetch accounts. Please contact your administrator", "error");
                });
            }
        },
        async getAccounts(){
            await this.$store
                .dispatch("accounts/getAllAccounts")
                .then((res) => {
                    if (res.data.error) {
                        return this.showToast(res.data.errorMessage, "error");
                    }
                    this.accounts = res.data;
                })
            .catch((error) => {
                this.showToast("Cannot fetch accounts. Please contact your administrator", "error");
            });
        },
        async getFundClusters(){
            await this.$store
                .dispatch("fundcluster/GetFundCluster")
                .then((res) => {
                    const isAdmin = this.$store.state.decrypt.isAdmin();
                    if (isAdmin) {
                        this.fcOptions = res.data;
                    } else {
                        const fcAccess = this.$store.state.decrypt.userAccessList()
                        .find((item) => item.Type.toLowerCase() === "finance" && item.Access.toLowerCase().includes("fund cluster"));
                            if(fcAccess){
                                const filterAccess = this.$store.state.decrypt.userAccessList()
                                .filter((item) => item.Type.toLowerCase() === "finance" && item.ParentId == fcAccess.Id);
                                filterAccess.forEach((item) => {
                                    this.fundClusterAccessList.push(
                                    Number(item.Access.split("-")[0])
                                );
                            });
                            this.fcOptions = res.data.filter((item) => this.fundClusterAccessList.includes(item.id));
                        }
                    }

                    this.fundClusters = this.fcOptions.map((obj) => ({
                        value: obj.id,
                        text: obj.cluster,
                    }));
                    this.fundClusters.unshift({
                        value: null,
                        text: "Select Fund cluster...",
                    });
                    this.clusterOptionforTransfer = this.fundClusters;
                })
                .catch(() => {
                    this.alert = {
                        type: "danger",
                        message:
                        "Something went wrong! Cannot fetch fund clusters. Please contact your administrator.",
                    };
                });
        },
        async changeData() {
            this.datLoading = true;
            await this.getData(this.selectedFC, this.selectedAccount ? this.selectedAccount.id : null);
            this.datLoading = false;
        },
        searchAccount() {
            this.$bvModal.show("search-account-modal");
        },
        selectAccount(account) {
            this.selectedAccount = account;
            this.selectedAccountName = `${account.uacs} - ${account.accountName}`;
        },
        clearAccountFilter() {
            this.selectedAccount = null;
            this.selectedAccountName = "";
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP'
            }).format(value);
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        },
        exportToExcel() {
            // Implement Excel export functionality
            this.showToast("Excel export functionality to be implemented", "info");
        },
        printLedger() {
            this.$refs.printOpt.putOptions({
                apiUrl: '/api/Report/Finance/Ledger/General',
                routeVariables: [this.$store.state.data.selectedCampus.id, this.selectedFC, this.selectedAccount ? this.selectedAccount.id : 0],
                body: {
                    dateFrom: this.getDate(this.dateFrom),
                    dateTo: this.getDate(this.dateTo),
                },
            });
            this.$bvModal.show("print-options-modal");
        }
    }
};
</script>

<template>
    <Layout @changeCampus="changeData($event.id)">
        <PageHeader :title="title" :items="items" />
        
        <template v-if="loading">
            <div class="d-flex justify-content-between mb-3">
                <div class="d-flex gap-1">
                    <b-skeleton width="280px" height="35px"></b-skeleton>
                    <b-skeleton width="200px" height="35px"></b-skeleton>
                    <b-skeleton width="150px" height="35px"></b-skeleton>
                    <b-skeleton width="150px" height="35px"></b-skeleton>
                    <b-skeleton width="70px" height="35px"></b-skeleton>
                </div>
                <div class="d-flex gap-1">
                    <b-skeleton width="100px" height="35px"></b-skeleton>
                    <b-skeleton width="100px" height="35px"></b-skeleton>
                </div>
            </div>
            <div class="mb-2">
                <b-skeleton width="100%" height="50px"></b-skeleton>
            </div>
            <div class="mb-2" v-for="x in 10" :key="x">
                <b-skeleton width="100%" height="60px"></b-skeleton>
            </div>
        </template>
        
        <template v-else>
            <!-- Filter Controls -->
            <div class="card shadow-none border mb-3">
                <div class="card-body py-3">
                    <div class="row align-items-end">
                        <div class="col-md-3">
                            <label class="form-label mb-1">Fund Cluster</label>
                            <select 
                                class="form-select mt-2 mb-2"
                                v-model="selectedFC"
                            >
                                <option
                                    v-for="x in fundClusters"
                                    :key="x.value"
                                    :value="x.value"
                                >
                                    {{ x.text }}
                                </option>
                            </select>
                            <!-- <label class="form-label mb-1">Search Transactions</label>
                            <b-form-input
                                v-model="filter"
                                type="text"
                                placeholder="Search reference, particulars..."
                                class="form-control"
                            ></b-form-input> -->
                        </div>
                        <div class="col-md-3">
                            <label class="form-label mb-1">Account Filter</label>
                            <div class="input-group">
                                <b-form-input
                                    v-model="selectedAccountName"
                                    type="text"
                                    placeholder="Select account..."
                                    readonly
                                    class="form-control"
                                ></b-form-input>
                                <button 
                                    class="btn btn-outline-secondary" 
                                    type="button" 
                                    @click="searchAccount"
                                >
                                    <i class="bx bx-search"></i>
                                </button>
                                <button 
                                    class="btn btn-outline-danger" 
                                    type="button" 
                                    @click="clearAccountFilter"
                                    v-if="selectedAccount"
                                >
                                    <i class="bx bx-x"></i>
                                </button>
                            </div>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label mb-1">Date From</label>
                            <date-picker
                                id="dateFrom"
                                v-model="dateFrom"
                                :first-day-of-week="1"
                                lang="en"
                                placeholder="Enter Date From..."
                            >
                            </date-picker>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label mb-1">Date To</label>
                            <date-picker
                                id="dateFrom"
                                v-model="dateTo"
                                :first-day-of-week="1"
                                lang="en"
                                placeholder="Enter Date From..."
                            >
                            </date-picker>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label mb-1">Items per page</label>
                            <b-form-select
                                class="form-select"
                                v-model="perPage"
                                :options="pageOptions"
                            ></b-form-select>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card border-success shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-success mb-1">Total Debit</h6>
                                    <h4 class="mb-0">{{ formatCurrency(totalDebit) }}</h4>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-success">
                                        <i class="bx bx-trending-up font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-danger shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-danger mb-1">Total Credit</h6>
                                    <h4 class="mb-0">{{ formatCurrency(totalCredit) }}</h4>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-danger">
                                        <i class="bx bx-trending-down font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-info shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-info mb-1">Current Balance</h6>
                                    <h4 class="mb-0">{{ formatCurrency(currentBalance) }}</h4>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-info">
                                        <i class="bx bx-wallet font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3">
                    <div class="card border-warning shadow-none border">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h6 class="text-warning mb-1">Total Transactions</h6>
                                    <h4 class="mb-0">{{ totalRows }}</h4>
                                </div>
                                <div class="avatar-sm">
                                    <span class="avatar-title rounded-circle bg-warning">
                                        <i class="bx bx-list-ul font-size-18"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <template v-if="datLoading">
                <div class="mb-2">
                    <b-skeleton width="100%" height="50px"></b-skeleton>
                </div>
                <div class="mb-2" v-for="x in 10" :key="x">
                    <b-skeleton width="100%" height="60px"></b-skeleton>
                </div>
            </template>
            <template v-else>
                <!-- Action Buttons -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <div class="d-flex gap-2">
                        
                    </div>
                    <div class="d-flex gap-2">
                        <!-- <b-button variant="success" size="sm" @click="exportToExcel">
                            <i class="bx bx-download me-1"></i>Export Excel
                        </b-button> -->
                        <b-button variant="purple" size="sm" @click="printLedger" id="report-print-btn">
                            <i class="bx bx-printer me-1"></i>Print Ledger
                        </b-button>
                    </div>
                </div>

                <!-- No Data State -->
                <div v-if="!filteredTransactions.length" class="text-center py-5">
                    <div class="mb-3">
                        <i class="bx bx-folder-open font-size-48 text-muted"></i>
                    </div>
                    <h4 class="text-muted">No Transactions Found</h4>
                    <p class="text-muted">No transactions match the current filter criteria.</p>
                </div>

                <!-- Transactions Table -->
                <div v-else class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th class="text-center">Date</th>
                                <th class="text-center">Reference</th>
                                <th class="text-center">Particulars</th>
                                <th class="text-center">Debit</th>
                                <th class="text-center">Credit</th>
                                <th class="text-center">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="parent in transactions">
                                <tr :key="parent.accountId">
                                    <td colspan="100%" class="font-weight-bold text-white">{{parent.uacs}} - {{parent.account}}</td>
                                </tr>
                                <template v-for="child in parent.generalLedgerTransactions">
                                    <tr :key="`${parent.accountId}-${child.id}`">
                                        <td class="text-center">
                                            {{ formatDate(child.transactionDate) }}
                                        </td>
                                        <td class="text-center">
                                            <span class="text-primary fw-medium">{{ child.reference }}</span>
                                        </td>
                                        <td class="text-center">
                                            <span :title="child.particulars">
                                                {{ child.particulars }}
                                            </span>
                                        </td>
                                        <td class="text-right">
                                            <span class="text-success fw-medium" v-if="child.debit">
                                                {{ formatCurrency(child.debit) }}
                                            </span>
                                            <span class="text-muted" v-else>-</span>
                                        </td>
                                        <td class="text-right">
                                            <span class="text-danger fw-medium" v-if="child.credit">
                                                {{ formatCurrency(child.credit) }}
                                            </span>
                                            <span class="text-muted" v-else>-</span>
                                        </td>
                                        <td class="text-right">
                                            <span class=" fw-medium" v-if="child.balance">
                                                {{ formatCurrency(child.balance) }}
                                            </span>
                                            <span class="text-muted" v-else>-</span>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="row mt-3" v-if="totalRows > perPage">
                    <div class="col">
                        <div class="dataTables_paginate paging_simple_numbers float-end">
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalRows"
                                :per-page="perPage"
                                align="right"
                                size="sm"
                                class="mb-0"
                            ></b-pagination>
                        </div>
                    </div>
                </div>
            </template>
        </template>
        
        <print-options ref="printOpt"></print-options>
        <!-- Account Search Modal -->
        <AccountModal 
            :accounts="accounts"
            @select-account="selectAccount"
        />
    </Layout>
</template>

<style scoped>
.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
}

.avatar-sm {
    width: 2.5rem;
    height: 2.5rem;
}

.avatar-title {
    width: 100%;
    height: 100%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>